import { lazy } from 'react'
import { Navigate } from 'react-router-dom'
import { isAuthenticated } from '../hooks/CookiesHook'
import { getFirstAllowedNaiFlowDashboardPath, getLocalStorage, hasPermission, homeScreenRedirection, naiFlowAuthTab } from '../utils/helper'
import path from 'path'
import DashboardLayout from '../components/UI/Layouts/DashboardLayout'
import LRFLayout from '../components/UI/Layouts/LRFLayout'
import ProjectLayout from '../components/UI/Layouts/ProjectLayout'
import TabsLayout from '../components/UI/Layouts/TabsLayout'
import UserManagementLayout from '../components/UI/Layouts/UserManagementLayout'
import CopilotLayout from '../components/UI/Layouts/CopilotLayout'
import SidebarLayout from '../components/UI/Layouts/SidebarLayout'

// Lazy load pages
const PageNotFound = lazy(() => import('../pages/PageNotFound'))
const LoginOld = lazy(() => import('../pages/Login-old'))
const Login = lazy(() => import('../pages/Login'))
const OTPVerification = lazy(() => import('../pages/OTPVerification'))
const SelectWorkspace = lazy(() => import('../pages/SelectWorkspace'))
const OTPVerificationOld = lazy(() => import('../pages/OTPVerification-old'))
const SelectWorkspaceOld = lazy(() => import('../pages/SelectWorkspace-old'))
const ChangeWorkspace = lazy(() => import('../pages/ChangeWorkspace'))
// Dashboard
const Dashboard = lazy(() => import('../pages/Dashboard'))
const ObservationsListing = lazy(() => import('../pages/Dashboard/Listings/Observations'))
const IncidentsListing = lazy(() => import('../pages/Dashboard/Listings/Incidents'))
const InspectionsListing = lazy(() => import('../pages/Dashboard/Listings/Inspections'))
const TrainingsListing = lazy(() => import('../pages/Dashboard/Listings/Trainings'))
const SiteWalkthroughDashboard = lazy(() => import('../pages/Dashboard/SiteWalkthroughDashboard'))
const StatisticsCaptureDashboard = lazy(() => import('../pages/Dashboard/StatisticsCaptureDashboard'))
const IncidentDashboard = lazy(() => import('../pages/Dashboard/IncidentDashboard'))
const InspectionDashboard = lazy(() => import('../pages/Dashboard/InspectionDashboard'))
const TrainingDashboard = lazy(() => import('../pages/Dashboard/TrainingDashboard'))
const TrainingDashboardNew = lazy(() => import('../pages/Dashboard/TrainingDashboardNew'))
const OverviewDashboard = lazy(() => import('../pages/Dashboard/OverviewDashboard'))
const OverviewDashboardNew = lazy(() => import('../pages/Dashboard/OverviewDashboardNew'))
const ObservationDashboardNew = lazy(() => import('../pages/Dashboard/ObservationDashboardNew'))
const IncidentDashboardNew = lazy(() => import('../pages/Dashboard/IncidentDashboardNew'))
const AIInsights = lazy(() => import('../pages/Dashboard/AIInsights'))

/** Redirects /dashboard/nai-flow to the first nAI Flow tab the user has permission for. */
const NaiFlowDashboardRedirect = () => <Navigate to={getFirstAllowedNaiFlowDashboardPath()} replace />

/** If user has no Observation tab permission, redirect to first allowed nAI Flow tab instead of rendering ObservationDashboardNew. */
const ObservationRouteGuard = () => {
  const permissions = JSON.parse(getLocalStorage('permissions') || '[]')
  if (!hasPermission(permissions, 'observations-dashboard', 1)) {
    return <Navigate to={getFirstAllowedNaiFlowDashboardPath()} replace />
  }
  return (
    <TabsLayout
      breadcrumbData={[
        { title: 'Dashboard', redirect: '/dashboard/nai-flow' },
        { title: 'nAI Flow', redirect: '/dashboard/nai-flow' },
        { title: 'Observation', redirect: '/dashboard/nai-flow/observation' },
      ]}
      page="nai-flow-dashboard"
    >
      <ObservationDashboardNew />
    </TabsLayout>
  )
}

// Nai Hub Dashboard
const NaiHubDashboard = lazy(() => import('../pages/Dashboard/NaiHubDashboard'))
// User Management
const UserRole = lazy(() => import('../pages/UserManagement/UserRole'))
const UserListing = lazy(() => import('../pages/UserManagement/UserListing'))
const AddUserRole = lazy(() => import('../pages/UserManagement/AddUserRole'))
const AddUser = lazy(() => import('../pages/UserManagement/AddUser'))
// Quiz Assessment
const QuizAssessment = lazy(() => import('../pages/QuizAssessment'))
const QuizAssessmentDetails = lazy(() => import('../pages/QuizAssessment/QuizAssessmentDetails'))
// Video Script Generation
const VideoScriptGeneration = lazy(() => import('../pages/VideoScriptGeneration'))
// Project Management Start
const ProjectManagement = lazy(() => import('../pages/ProjectManagement'))
const AddProject = lazy(() => import('../pages/ProjectManagement/AddProject'))
// Project's Rules And Escalation
const RulesAndEscalation = lazy(() => import('../pages/ProjectManagement/RulesAndEscalation'))
const AddRulesAndEscalation = lazy(() => import('../pages/ProjectManagement/RulesAndEscalation/AddRulesAndEscalation'))
// Project's User Management
const ProjectUsers = lazy(() => import('../pages/ProjectManagement/ProjectUsers'))
const AddUserToProject = lazy(() => import('../pages/ProjectManagement/ProjectUsers/AddUserToProject'))
const ProjectRoles = lazy(() => import('../pages/ProjectManagement/ProjectRoles'))
const AddRoleToProject = lazy(() => import('../pages/ProjectManagement/ProjectRoles/AddRoleToProject'))
// nAI Flow
const WhatsappGroup = lazy(() => import('../pages/ProjectManagement/NaiFlow/WhatsappGroup'))
const Observations = lazy(() => import('../pages/ProjectManagement/NaiFlow/Observations'))
const Incidents = lazy(() => import('../pages/ProjectManagement/NaiFlow/Incidents'))
const SiteWalkthrough = lazy(() => import('../pages/ProjectManagement/NaiFlow/SiteWalkthrough'))
const SiteWalkthroughDetails = lazy(() => import('../pages/ProjectManagement/NaiFlow/SiteWalkthrough/SiteWalkthroughDetails'))
const SiteWalkthroughView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/SiteWalkthrough'))
const SiteWalkthroughDetailsView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/SiteWalkthrough/SiteWalkthroughDetailsView'))
const ObservationDetails = lazy(() => import('../pages/ProjectManagement/NaiFlow/Observations/ObservationDetails'))
const IncidentDetails = lazy(() => import('../pages/ProjectManagement/NaiFlow/Incidents/IncidentDetails'))
const Inspections = lazy(() => import('../pages/ProjectManagement/NaiFlow/Inspections'))
const NonConformance = lazy(() => import('../pages/ProjectManagement/NaiFlow/Inspections/NonConformance'))
const NonConformanceDetails = lazy(() => import('../pages/ProjectManagement/NaiFlow/Inspections/NonConformance/NonConformanceDetails'))
const InspectionDetails = lazy(() => import('../pages/ProjectManagement/NaiFlow/Inspections/InspectionDetails'))
const Schedule = lazy(() => import('../pages/ProjectManagement/NaiFlow/Inspections/Schedule'))
const AddInspectionSchedule = lazy(() => import('../pages/ProjectManagement/NaiFlow/Inspections/Schedule/AddInspectionSchedule'))
const Trainings = lazy(() => import('../pages/ProjectManagement/NaiFlow/Trainings'))
const TrainingDetails = lazy(() => import('../pages/ProjectManagement/NaiFlow/Trainings/TrainingDetails'))
// statistics Capture
const StatisticsCapture = lazy(() => import('../pages/ProjectManagement/NaiFlow/StatisticsCapture/Schedule'))
const AddSchedule = lazy(() => import('../pages/ProjectManagement/NaiFlow/StatisticsCapture/Schedule/AddSchedule'))
const StatisticsCaptureReport = lazy(() => import('../pages/ProjectManagement/NaiFlow/StatisticsCapture/StatisticsCaptureReport'))
const EditReport = lazy(() => import('../pages/ProjectManagement/NaiFlow/StatisticsCapture/StatisticsCaptureReport/EditReport'))

// nAI Flow VIew
const WhatsappGroupView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/WhatsappGroup'))
const ObservationsView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/Observations'))
const IncidentsView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/Incidents'))
const ObservationDetailsView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/Observations/ObservationDetails'))
const IncidentDetailsView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/Incidents/IncidentDetails'))
const InspectionsView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/Inspections'))
const NonConformanceView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/Inspections/NonConformance'))
const NonConformanceDetailsView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/Inspections/NonConformance/NonConformanceDetails'))
const InspectionDetailsView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/Inspections/InspectionDetails'))
const ScheduleView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/Inspections/Schedule'))
const AddInspectionScheduleView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/Inspections/Schedule/AddInspectionSchedule'))
const TrainingsView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/Trainings'))
const TrainingDetailsView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/Trainings/TrainingDetails'))
// statistics Capture
const StatisticsCaptureView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/StatisticsCapture/Schedule'))
const AddScheduleView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/StatisticsCapture/Schedule/AddSchedule'))
const StatisticsCaptureReportView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/StatisticsCapture/StatisticsCaptureReport'))
const EditReportView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/StatisticsCapture/StatisticsCaptureReport/EditReport'))

// nAI Hub
const NaiHub = lazy(() => import('../pages/ProjectManagement/NaiHub'))
const AddNaiHub = lazy(() => import('../pages/ProjectManagement/NaiHub/AddNaiHub'))
// const NaiHubDashboard = lazy(() => import('../pages/ProjectManagement/NaiHub/NaiHubDashboard'))

const ProjectWorkflow = lazy(() => import('../pages/ProjectManagement/ProjectWorkflow'))
// Project Management End

// Workflow Builder
const WorkflowBuilder = lazy(() => import('../pages/WorkflowBuilder'))
const EditWorkflow = lazy(() => import('../pages/WorkflowBuilder/EditWorkflow'))

// AI RAMS Assessment
const AiRamsAssessment = lazy(() => import('../pages/AiRamsAssessment'))

// Masters pages
const Division = lazy(() => import('../pages/Masters/Division'))
const HazardType = lazy(() => import('../pages/Masters/HazardType'))
const RootCause = lazy(() => import('../pages/Masters/RootCause'))
const Severity = lazy(() => import('../pages/Masters/Severity'))
const ObservationStatus = lazy(() => import('../pages/Masters/ObservationStatus'))
const StatisticsCaptureStatus = lazy(() => import('../pages/Masters/StatisticsCaptureStatus'))
const StatisticsCaptureInputs = lazy(() => import('../pages/Masters/StatisticsCaptureInputs'))
const IncidentStatus = lazy(() => import('../pages/Masters/IncidentStatus'))
const IncidentCategory = lazy(() => import('../pages/Masters/IncidentCategory'))
const IncidentSeverity = lazy(() => import('../pages/Masters/IncidentSeverity'))
const InspectionStatus = lazy(() => import('../pages/Masters/InspectionStatus'))
const InspectionScheduleStatus = lazy(() => import('../pages/Masters/InspectionScheduleStatus'))
const InspectionNonConformanceStatus = lazy(() => import('../pages/Masters/InspectionNonConformanceStatus'))
const InspectionSubCategory = lazy(() => import('../pages/Masters/InspectionSubCategory'))
const InspectionTopic = lazy(() => import('../pages/Masters/InspectionTopic'))
const InspectionQuestions = lazy(() => import('../pages/Masters/InspectionQuestions'))
const TrainingCategory = lazy(() => import('../pages/Masters/TrainingCategory'))

// Lazy load components
const ComingSoon = lazy(() => import('../components/ComingSoon'))
const CheckAppInstalled = lazy(() => import('../pages/CheckAppInstalled'))
const SSO = lazy(() => import('../pages/SSO'))
// if already authenticated and try to access login page, redirect to dashboard
const PublicRoute = ({ children }: any) => {
  const permission_details = getLocalStorage('permissions') ? JSON.parse(`${getLocalStorage('permissions')}`) : []

  let redirectPath = homeScreenRedirection()
  const paths = [
    { path: naiFlowAuthTab, slug: 'dashboard' },
    { path: '/reports', slug: 'reports' },
    { path: '/user-management/user-role', slug: 'user-management' },
    { path: '/user-management/user-role', slug: 'user' },
    { path: '/project-management', slug: 'project-management' },
    { path: '/project-management', slug: 'project-list' },
  ] as any
  if (!permission_details?.find((p: any) => p?.slug === 'observations')) {
    const firstSlug = permission_details[0]?.slug
    if (paths?.find((p: any) => p?.slug === firstSlug) && firstSlug) {
      redirectPath = paths?.find((p: any) => p?.slug === firstSlug)?.path
    }
  }

  return isAuthenticated() ? <Navigate to={redirectPath} /> : children
}

// if not authenticated and try to access protected page, redirect to login
const ProtectedRoute = ({ children }: any) => {
  return isAuthenticated() ? children : <Navigate to="/login" />
}

const wrapRoutesWithProtectedRoute = (routes: any) => {
  // Map over the routes array
  return routes.map((route: any) => {
    // Check if the route has children
    if (route.children) {
      // If it has children, recursively wrap the children with protected route.
      // Also guard the parent's own element (e.g. <SidebarLayout/>) so an unauthenticated user is
      // redirected to /login BEFORE the layout mounts — otherwise the layout renders (and can crash)
      // before the child route's ProtectedRoute gets a chance to redirect.
      const children = wrapRoutesWithProtectedRoute(route.children)
      return {
        ...route,
        element: route?.private ? <ProtectedRoute key={route.path}>{route.element}</ProtectedRoute> : route.element,
        children,
      }
    } else {
      // If the route does not have children, determine whether to wrap it with protected or public route
      return {
        ...route,
        element: route?.private ? <ProtectedRoute key={route.path}>{route.element}</ProtectedRoute> : <PublicRoute key={route.path}>{route.element}</PublicRoute>,
      }
    }
  })
}

export const routes = [
  ...wrapRoutesWithProtectedRoute([
    {
      path: '',
      private: false,
      element: <Login />,
    },
    {
      path: 'login-old',
      private: false,
      element: (
        <LRFLayout>
          <LoginOld />
        </LRFLayout>
      ),
    },
    {
      path: 'login',
      private: false,
      element: <Login />,
    },
    {
      path: 'otp-verification',
      private: false,
      element: <OTPVerification />,
    },
    {
      path: 'select-workspace',
      private: false,
      element: <SelectWorkspace />,
    },
    {
      path: 'otp-verification-old',
      private: false,
      element: (
        <LRFLayout>
          <OTPVerificationOld />
        </LRFLayout>
      ),
    },
    {
      path: 'select-workspace-old',
      private: false,
      element: (
        <LRFLayout>
          <SelectWorkspaceOld />
        </LRFLayout>
      ),
    },
    {
      path: 'change-workspace',
      private: true,
      element: <SidebarLayout />,
      children: [
        {
          path: '',
          private: true,
          element: (
            <DashboardLayout>
              <ChangeWorkspace />
            </DashboardLayout>
          ),
        },
      ],
    },
    // New Dashboard flow
    {
      path: 'dashboard/',
      private: true,
      element: <SidebarLayout />,
      children: [
        {
          path: 'overview',
          private: true,
          element: (
            <DashboardLayout page={'nai-flow-dashboard'}>
              <OverviewDashboardNew />
            </DashboardLayout>
          ),
        },
        {
          path: 'overview-old',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Dashboard', redirect: '/dashboard/observation' },
                { title: 'Overview', redirect: '/dashboard/overview-old' },
              ]}
              page={'nai-flow-dashboard'}
            >
              <OverviewDashboard />
            </DashboardLayout>
          ),
        },
        {
          path: 'nai-flow/observation-old',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Dashboard', redirect: '/dashboard/nai-flow/observation' },
                { title: 'nAI Flow', redirect: '/dashboard/nai-flow/observation' },
                { title: 'Observation', redirect: '/dashboard/nai-flow/observation' },
              ]}
              page={'nai-flow-dashboard'}
            >
              <Dashboard />
            </TabsLayout>
          ),
        },
        {
          path: 'nai-flow',
          private: true,
          element: <NaiFlowDashboardRedirect />,
        },
        {
          path: 'nai-flow/observation',
          private: true,
          element: <ObservationRouteGuard />,
        },
        {
          path: 'nai-flow/observations-listing',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Dashboard', redirect: '/dashboard' },
                { title: 'nAI Flow', redirect: '/dashboard/nai-flow' },
                { title: 'Observations', redirect: '/dashboard/nai-flow/observations-listing' },
              ]}
            >
              <ObservationsListing />
            </DashboardLayout>
          ),
        },
        {
          path: 'nai-flow/incidents-listing',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Dashboard', redirect: '/dashboard' },
                { title: 'nAI Flow', redirect: '/dashboard/nai-flow/incident' },
                { title: 'Incidents', redirect: '/dashboard/nai-flow/incidents-listing' },
              ]}
            >
              <IncidentsListing />
            </DashboardLayout>
          ),
        },
        {
          path: 'nai-flow/inspections-listing',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Dashboard', redirect: '/dashboard' },
                { title: 'nAI Flow', redirect: '/dashboard/nai-flow/inspection' },
                { title: 'Inspections', redirect: '/dashboard/nai-flow/inspections-listing' },
              ]}
            >
              <InspectionsListing />
            </DashboardLayout>
          ),
        },
        {
          path: 'nai-flow/trainings-listing',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Dashboard', redirect: '/dashboard' },
                { title: 'nAI Flow', redirect: '/dashboard/nai-flow/training' },
                { title: 'Trainings', redirect: '/dashboard/nai-flow/trainings-listing' },
              ]}
            >
              <TrainingsListing />
            </DashboardLayout>
          ),
        },
        {
          path: '',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Dashboard', redirect: '/dashboard' },
                { title: 'Observations', redirect: '/dashboard' },
              ]}
            >
              <Dashboard />
            </DashboardLayout>
          ),
        },
        {
          path: 'site-walkthrough',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Dashboard', redirect: '/dashboard' },
                { title: 'Site Walkthrough', redirect: '/dashboard' },
              ]}
            >
              <SiteWalkthroughDashboard />
            </DashboardLayout>
          ),
        },
        {
          path: 'nai-flow/statistics-capture',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Dashboard', redirect: '/dashboard/nai-flow' },
                { title: 'nAI Flow', redirect: '/dashboard/nai-flow' },
                { title: 'Statistics Capture', redirect: '/dashboard/nai-flow/statistics-capture' },
              ]}
              page={'nai-flow-dashboard'}
            >
              <StatisticsCaptureDashboard />
            </TabsLayout>
          ),
        },
        {
          path: 'nai-flow/incident',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Dashboard', redirect: '/dashboard/nai-flow' },
                { title: 'nAI Flow', redirect: '/dashboard/nai-flow' },
                { title: 'Incident', redirect: '/dashboard/nai-flow/incident' },
              ]}
              page={'nai-flow-dashboard'}
            >
              <IncidentDashboardNew />
            </TabsLayout>
          ),
        },
        {
          path: 'nai-flow/incident-old',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Dashboard', redirect: '/dashboard/nai-flow' },
                { title: 'nAI Flow', redirect: '/dashboard/nai-flow' },
                { title: 'Incident (Old)', redirect: '/dashboard/nai-flow/incident-old' },
              ]}
              page={'nai-flow-dashboard'}
            >
              <IncidentDashboard />
            </TabsLayout>
          ),
        },
        {
          path: 'nai-flow/inspection',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Dashboard', redirect: '/dashboard/nai-flow' },
                { title: 'nAI Flow', redirect: '/dashboard/nai-flow' },
                { title: 'Inspection', redirect: '/dashboard/nai-flow/inspection' },
              ]}
              page={'nai-flow-dashboard'}
            >
              <InspectionDashboard />
            </TabsLayout>
          ),
        },
        {
          path: 'nai-flow/training',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Dashboard', redirect: '/dashboard/nai-flow' },
                { title: 'nAI Flow', redirect: '/dashboard/nai-flow' },
                { title: 'Training', redirect: '/dashboard/nai-flow/training' },
              ]}
              page={'nai-flow-dashboard'}
            >
              <TrainingDashboardNew />
            </TabsLayout>
          ),
        },
        {
          path: 'nai-flow/training-old',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Dashboard', redirect: '/dashboard/nai-flow' },
                { title: 'nAI Flow', redirect: '/dashboard/nai-flow' },
                { title: 'Training (Old)', redirect: '/dashboard/nai-flow/training-old' },
              ]}
              page={'nai-flow-dashboard'}
            >
              <TrainingDashboard />
            </TabsLayout>
          ),
        },
        {
          path: 'nai-hub',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Dashboard', redirect: '/dashboard/nai-flow' },
                { title: 'nAI Hub', redirect: '/dashboard/nai-hub' },
              ]}
            >
              <ComingSoon />
            </DashboardLayout>
          ),
        },
        // NaiHub Dashboard
        {
          path: 'nai-hub/:name/:id',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Dashboard', redirect: '/dashboard/nai-flow' },
                { title: 'nAI Hub', redirect: '/dashboard/nai-hub/observation' },
              ]}
              page={'nai-hub-dashboard'}
            >
              <NaiHubDashboard />
            </TabsLayout>
          ),
        },
      ],
    },
    {
      path: 'ai-insights',
      private: true,
      element: <SidebarLayout />,
      children: [
        {
          path: '',
          private: true,
          element: (
            <DashboardLayout breadcrumbData={[{ title: 'AI Insights', redirect: '/ai-insights' }]}>
              <AIInsights />
            </DashboardLayout>
          ),
        },
      ],
    },
    {
      path: 'reports',
      private: true,
      element: <SidebarLayout />,
      children: [
        {
          path: '',
          private: true,
          element: (
            <DashboardLayout>
              <div className="h-full p-8">
                <ComingSoon pageName="Statistics Capture" />
              </div>
            </DashboardLayout>
          ),
        },
      ],
    },
    // User Management
    {
      path: 'user-management',
      private: true,
      element: <SidebarLayout />,
      children: [
        {
          path: 'user-role',
          private: true,
          element: (
            <UserManagementLayout
              defaultIndex={1}
              breadcrumbData={[
                { title: 'User Management', redirect: '/user-management/user-role' },
                { title: 'User Role', redirect: '/user-management/user-role' },
              ]}
            >
              <UserRole />
            </UserManagementLayout>
          ),
        },
        {
          path: 'user-role/:id',
          private: true,
          element: (
            <UserManagementLayout
              defaultIndex={1}
              breadcrumbData={[
                { title: 'User Management', redirect: '/user-management/user-role' },
                { title: 'User Role', redirect: '/user-management/user-role' },
              ]}
            >
              <AddUserRole />
            </UserManagementLayout>
          ),
        },
        {
          path: 'users',
          private: true,
          element: (
            <UserManagementLayout
              defaultIndex={0}
              breadcrumbData={[
                { title: 'User Management', redirect: '/user-management/users' },
                { title: 'Users', redirect: '/user-management/users' },
              ]}
            >
              <UserListing />
            </UserManagementLayout>
          ),
        },
        {
          path: 'users/:id',
          private: true,
          element: (
            <UserManagementLayout
              defaultIndex={0}
              breadcrumbData={[
                { title: 'User Management', redirect: '/user-management/users' },
                { title: 'Users', redirect: '/user-management/users' },
              ]}
            >
              <AddUser />
            </UserManagementLayout>
          ),
        },
      ],
    },
    // Quiz Assessment
    {
      path: 'quiz-assessment',
      private: true,
      element: <SidebarLayout />,
      children: [
        {
          path: '',
          private: true,
          element: (
            <DashboardLayout breadcrumbData={[{ title: 'Quiz Assessment', redirect: '/quiz-assessment' }]}>
              <QuizAssessment />
            </DashboardLayout>
          ),
        },
        {
          path: ':id',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Quiz Assessment', redirect: '/quiz-assessment' },
                { title: 'Details', redirect: '/quiz-assessment/:id' },
              ]}
            >
              <QuizAssessmentDetails />
            </DashboardLayout>
          ),
        },
      ],
    },
    // Video Script Generation (permission slug: video-script-generation)
    {
      path: 'video-script-generation',
      private: true,
      element: <SidebarLayout />,
      children: [
        {
          path: '',
          private: true,
          element: (
            <DashboardLayout breadcrumbData={[{ title: 'Video Script Generation', redirect: '/video-script-generation' }]}>
              <VideoScriptGeneration />
            </DashboardLayout>
          ),
        },
      ],
    },
    // Project Management
    {
      path: 'project-management',
      private: true,
      element: <SidebarLayout />,
      children: [
        {
          path: '',
          private: true,
          element: (
            <DashboardLayout>
              <ProjectManagement />
            </DashboardLayout>
          ),
        },
        {
          path: 'add',
          private: true,
          element: (
            <DashboardLayout>
              <AddProject />
            </DashboardLayout>
          ),
        },
        {
          path: ':name/:id/details',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'Details', redirect: '/project-management/:name/:id/details' },
              ]}
              page={'project-page'}
            >
              <AddProject mode={'detail'} />
            </TabsLayout>
          ),
        },
        {
          path: ':name/:id/workflow',
          private: true,
          element: (
            <ProjectLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'Workflow', redirect: '/project-management/:name/:id/details' },
              ]}
            >
              <ProjectWorkflow />
            </ProjectLayout>
          ),
        },
        {
          path: ':name/:id/rules-and-escalation',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'Rules And Escalation', redirect: '/project-management/:name/:id/rules-and-escalation' },
              ]}
              page={'project-page'}
            >
              <RulesAndEscalation />
            </TabsLayout>
          ),
        },
        {
          path: ':name/:id/rules-and-escalation/add',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'Rules And Escalation', redirect: '/project-management/:name/:id/rules-and-escalation' },
                { title: 'Add', redirect: '/project-management/:name/:id/rules-and-escalation' },
              ]}
              page={'project-page'}
            >
              <AddRulesAndEscalation />
            </DashboardLayout>
          ),
        },
        {
          path: ':name/:id/rules-and-escalation/:subName/:subId',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'Rules And Escalation', redirect: '/project-management/:name/:id/rules-and-escalation' },
                { title: ':subName', redirect: '/project-management/:name/:id/rules-and-escalation' },
              ]}
              page={'project-page'}
            >
              <AddRulesAndEscalation />
            </DashboardLayout>
          ),
        },
        {
          path: ':name/:id/rules-and-escalation/duplicate/:subName/:subId',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'Rules And Escalation', redirect: '/project-management/:name/:id/rules-and-escalation' },
                { title: ':subName', redirect: '/project-management/:name/:id/rules-and-escalation' },
              ]}
              page={'project-page'}
            >
              <AddRulesAndEscalation isDuplicate={true} />
            </DashboardLayout>
          ),
        },
        {
          path: ':name/:id/user-management/users',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'User Management', redirect: '/project-management/:name/:id/user-management/:slug' },
                { title: 'Users', redirect: '/project-management/:name/:id/details' },
              ]}
              page={'project-user-management'}
            >
              <ProjectUsers />
            </TabsLayout>
          ),
        },
        {
          path: ':name/:id/user-management/users/add',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'User Management', redirect: '/project-management/:name/:id/user-management/:slug' },
                { title: 'Users', redirect: '/project-management/:name/:id/user-management/users' },
                { title: 'Add', redirect: '/project-management/:name/:id/user-management/users' },
              ]}
              page={'project-user-management'}
            >
              <AddUserToProject />
            </DashboardLayout>
          ),
        },
        {
          path: ':name/:id/user-management/users/:subName/:subId',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'User Management', redirect: '/project-management/:name/:id/user-management/:slug' },
                { title: 'Users', redirect: '/project-management/:name/:id/user-management/users' },
                { title: ':subName', redirect: '/project-management/:name/:id/user-management/users' },
              ]}
              page={'project-user-management'}
            >
              <AddUserToProject />
            </DashboardLayout>
          ),
        },
        {
          path: ':name/:id/user-management/roles',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'User Management', redirect: '/project-management/:name/:id/user-management/:slug' },
                { title: 'Roles', redirect: '/project-management/:name/:id/details' },
              ]}
              page={'project-user-management'}
            >
              <ProjectRoles />
            </TabsLayout>
          ),
        },
        {
          path: ':name/:id/user-management/roles/add',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'User Management', redirect: '/project-management/:name/:id/user-management/:slug' },
                { title: 'Roles', redirect: '/project-management/:name/:id/user-management/roles' },
                { title: 'Add', redirect: '/project-management/:name/:id/user-management/roles' },
              ]}
              page={'project-user-management'}
            >
              <AddRoleToProject />
            </DashboardLayout>
          ),
        },
        {
          path: ':name/:id/user-management/roles/:subName/:subId',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'User Management', redirect: '/project-management/:name/:id/user-management/:slug' },
                { title: 'Roles', redirect: '/project-management/:name/:id/user-management/roles' },
                { title: ':subName', redirect: '/project-management/:name/:id/user-management/roles' },
              ]}
              page={'project-user-management'}
            >
              <AddRoleToProject />
            </DashboardLayout>
          ),
        },
        {
          path: ':name/:id/nai-flow/whatsapp-group',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                { title: 'Whatsapp Group', redirect: '/project-management/:name/:id/nai-flow' },
              ]}
              page={'nai-flow'}
            >
              <WhatsappGroup />
            </TabsLayout>
          ),
        },
        /*  {
       path: 'project-management/:name/:id/nai-flow-view/whatsapp-group',
       private: true,
       element: (
         <TabsLayout
           breadcrumbData={[
             { title: 'Project Management', redirect: '/project-management' },
             { title: ':name', redirect: '/project-management/:name/:id/details' },
             { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
             { title: 'Whatsapp Group', redirect: '/project-management/:name/:id/nai-flow' },
           ]}
           page={'nai-flow-view'}
         >
           <WhatsappGroupView />
         </TabsLayout>
       ),
     }, */
        {
          path: ':name/:id/nai-flow/observations',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                { title: 'Observations', redirect: '/project-management/:name/:id/nai-flow/observations' },
              ]}
              page={'nai-flow'}
            >
              <Observations />
            </TabsLayout>
          ),
        },
        {
          path: ':name/:id/:subsidiaryId/nai-flow-view/observations',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                { title: 'Observations', redirect: '/project-management/:name/:id/nai-flow-view/observations' },
              ]}
              page={'nai-flow-view'}
            >
              <ObservationsView />
            </TabsLayout>
          ),
        },
        {
          path: ':name/:id/:subsidiaryId/nai-flow-view/observations/:subName/:subId',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                { title: 'Observations', redirect: '/project-management/:name/:id/nai-flow-view/Observations' },
                { title: ':subName', redirect: '/project-management/:name/:id/nai-flow-view/observations' },
              ]}
              page={'nai-flow-view'}
              disableBreadcrumbLinks={true}
            >
              <ObservationDetailsView />
            </DashboardLayout>
          ),
        },
        // Linked Projects - Incidents
        {
          path: ':name/:id/:subsidiaryId/nai-flow-view/incidents',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                { title: 'Incidents', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/incidents' },
              ]}
              page={'nai-flow-view'}
            >
              <IncidentsView />
            </TabsLayout>
          ),
        },
        {
          path: ':name/:id/:subsidiaryId/nai-flow-view/incidents/:subName/:subId',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                { title: 'Incidents', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/incidents' },
                { title: ':subName', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/incidents' },
              ]}
              page={'nai-flow-view'}
              disableBreadcrumbLinks={true}
            >
              <IncidentDetailsView />
            </DashboardLayout>
          ),
        },
        // Linked Projects - Inspections
        {
          path: ':name/:id/:subsidiaryId/nai-flow-view/inspections/schedule',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                { title: 'Inspections', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule' },
                { title: 'Schedule', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule' },
              ]}
              page={'nai-flow-view'}
            >
              <ScheduleView />
            </TabsLayout>
          ),
        },
        {
          path: ':name/:id/:subsidiaryId/nai-flow-view/inspections/list',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                { title: 'Inspections', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule' },
                { title: 'Inspections List', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule' },
              ]}
              page={'nai-flow-view'}
            >
              <InspectionsView />
            </TabsLayout>
          ),
        },
        {
          path: ':name/:id/:subsidiaryId/nai-flow-view/inspections/non-conformance',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                { title: 'Inspections', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule' },
                { title: 'Non-Conformance', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/non-conformance' },
              ]}
              page={'nai-flow-view'}
            >
              <NonConformanceView />
            </TabsLayout>
          ),
        },
        // Linked Projects - Trainings
        {
          path: ':name/:id/:subsidiaryId/nai-flow-view/trainings',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                { title: 'Trainings', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/trainings' },
              ]}
              page={'nai-flow-view'}
            >
              <TrainingsView />
            </TabsLayout>
          ),
        },
        // Linked Projects - Training Details
        {
          path: ':name/:id/:subsidiaryId/nai-flow-view/trainings/:subName/:subId',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                { title: 'Trainings', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/trainings' },
                { title: ':subName', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/trainings' },
              ]}
              page={'nai-flow-view'}
              disableBreadcrumbLinks={true}
            >
              <TrainingDetailsView />
            </DashboardLayout>
          ),
        },
        // Linked Projects - Site Walkthrough
        {
          path: ':name/:id/:subsidiaryId/nai-flow-view/site-walkthrough',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                { title: 'Site Walkthrough', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/site-walkthrough' },
              ]}
              page={'nai-flow-view'}
            >
              <SiteWalkthroughView />
            </TabsLayout>
          ),
        },
        {
          path: ':name/:id/:subsidiaryId/nai-flow-view/site-walkthrough/:subName/:subId',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                { title: 'Site Walkthrough', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/site-walkthrough' },
                { title: ':subName', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/site-walkthrough?site_walkthrough_id=:subName' },
                { title: ':subId', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/site-walkthrough' },
              ]}
              page={'nai-flow-view'}
            >
              <SiteWalkthroughDetailsView />
            </DashboardLayout>
          ),
        },
        // Linked Projects - Inspection Details
        {
          path: ':name/:id/:subsidiaryId/nai-flow-view/inspections/list/:subName/:subId',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                { title: 'Inspections', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule' },
                { title: 'Inspections List', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/list' },
                { title: ':subName', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/list' },
              ]}
              page={'nai-flow-view'}
            >
              <InspectionDetailsView />
            </DashboardLayout>
          ),
        },
        // Linked Projects - Statistics Capture
        {
          path: ':name/:id/:subsidiaryId/nai-flow-view/statistics-capture/schedule',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                { title: 'Statistics Capture', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/statistics-capture/schedule' },
                { title: 'Schedule', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/statistics-capture/schedule' },
              ]}
              page={'nai-flow-view'}
            >
              <StatisticsCaptureView />
            </TabsLayout>
          ),
        },
        {
          path: ':name/:id/:subsidiaryId/nai-flow-view/statistics-capture/reports',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                { title: 'Statistics Capture', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/statistics-capture/schedule' },
                { title: 'Reports', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/statistics-capture/reports' },
              ]}
              page={'nai-flow-view'}
            >
              <StatisticsCaptureReportView />
            </TabsLayout>
          ),
        },
        // Linked Projects - Add Inspection Schedule
        {
          path: ':name/:id/:subsidiaryId/nai-flow-view/inspections/schedule/add',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                { title: 'Inspections', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule' },
                { title: 'Schedule', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule' },
                { title: 'Add Schedule', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule' },
              ]}
              page={'nai-flow-view'}
            >
              <AddInspectionScheduleView />
            </DashboardLayout>
          ),
        },
        // Linked Projects - Add Statistics Capture Schedule
        {
          path: ':name/:id/:subsidiaryId/nai-flow-view/statistics-capture/schedule/add',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                { title: 'Statistics Capture', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/statistics-capture/schedule' },
                { title: 'Add Schedule', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/statistics-capture/schedule' },
              ]}
              page={'nai-flow-view'}
            >
              <AddScheduleView />
            </DashboardLayout>
          ),
        },
        // Linked Projects - Statistics Capture Schedule Details
        {
          path: ':name/:id/:subsidiaryId/nai-flow-view/statistics-capture/schedule/:subName/:subId',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                { title: 'Statistics Capture', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/statistics-capture/schedule' },
                { title: ':subName', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/statistics-capture/schedule' },
              ]}
              page={'nai-flow-view'}
            >
              <StatisticsCaptureView />
            </DashboardLayout>
          ),
        },
        // Linked Projects - Whatsapp Group
        {
          path: ':name/:id/:subsidiaryId/nai-flow-view/whatsapp-group',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                { title: 'Whatsapp Group', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/whatsapp-group' },
              ]}
              page={'nai-flow-view'}
            >
              <WhatsappGroupView />
            </TabsLayout>
          ),
        },
        {
          path: ':name/:id/nai-flow/observations/:subName/:subId',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                { title: 'Observations', redirect: '/project-management/:name/:id/nai-flow/Observations' },
                { title: ':subName', redirect: '/project-management/:name/:id/nai-flow/observations' },
              ]}
              page={'nai-flow'}
            >
              <ObservationDetails />
            </DashboardLayout>
          ),
        },
        {
          path: ':name/:id/nai-flow-view/observations/:subName/:subId',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                { title: 'Observations', redirect: '/project-management/:name/:id/nai-flow-view/Observations' },
                { title: ':subName', redirect: '/project-management/:name/:id/nai-flow-view/observations' },
              ]}
              page={'nai-flow-view'}
            >
              <ObservationDetailsView />
            </DashboardLayout>
          ),
        },
        {
          path: ':name/:id/nai-flow/incidents',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                { title: 'Incidents', redirect: '/project-management/:name/:id/nai-flow/incidents' },
              ]}
              page={'nai-flow'}
            >
              <Incidents />
            </TabsLayout>
          ),
        },
        {
          path: ':name/:id/nai-flow/site-walkthrough',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                { title: 'Site Walkthrough', redirect: '/project-management/:name/:id/nai-flow/site-walkthrough' },
              ]}
              page={'nai-flow'}
            >
              <SiteWalkthrough />
            </TabsLayout>
          ),
        },
        {
          path: ':name/:id/nai-flow/site-walkthrough/:subName/:subId',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                { title: 'Site Walkthrough', redirect: '/project-management/:name/:id/nai-flow/site-walkthrough' },
                { title: ':subName', redirect: '/project-management/:name/:id/nai-flow/site-walkthrough?site_walkthrough_id=:subName' },
                { title: ':subId', redirect: '/project-management/:name/:id/nai-flow/site-walkthrough' },
              ]}
              page={'nai-flow'}
            >
              <SiteWalkthroughDetails />
            </DashboardLayout>
          ),
        },
        {
          path: ':name/:id/nai-flow-view/incidents',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                { title: 'Incidents', redirect: '/project-management/:name/:id/nai-flow-view/incidents' },
              ]}
              page={'nai-flow-view'}
            >
              <IncidentsView />
            </TabsLayout>
          ),
        },
        {
          path: ':name/:id/nai-flow/incidents/:subName/:subId',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                { title: 'Incidents', redirect: '/project-management/:name/:id/nai-flow/incidents' },
                { title: ':subName', redirect: '/project-management/:name/:id/nai-flow/incidents' },
              ]}
              page={'nai-flow'}
            >
              <IncidentDetails />
            </DashboardLayout>
          ),
        },
        {
          path: ':name/:id/nai-flow-view/incidents/:subName/:subId',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                { title: 'Incidents', redirect: '/project-management/:name/:id/nai-flow-view/incidents' },
                { title: ':subName', redirect: '/project-management/:name/:id/nai-flow-view/incidents' },
              ]}
              page={'nai-flow-view'}
            >
              <IncidentDetailsView />
            </DashboardLayout>
          ),
        },
        {
          path: ':name/:id/nai-flow/inspections/list',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow/Inspections/schedule' },
                { title: 'Inspections List', redirect: '/project-management/:name/:id/nai-flow/Inspections/schedule' },
              ]}
              page={'inspections'}
              parentPage={'nai-flow'}
            >
              <Inspections />
            </TabsLayout>
          ),
        },
        {
          path: ':name/:id/nai-flow-view/inspections/list',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/schedule' },
                { title: 'Inspections List', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/schedule' },
              ]}
              page={'inspections'}
              parentPage={'nai-flow-view'}
            >
              <InspectionsView />
            </TabsLayout>
          ),
        },
        {
          path: ':name/:id/nai-flow/inspections/list/:subName/:subId',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow/Inspections/schedule' },
                { title: 'Inspections List', redirect: '/project-management/:name/:id/nai-flow/Inspections/list' },
                { title: ':subName', redirect: '/project-management/:name/:id/nai-flow/Inspections/list' },
              ]}
              page={'nai-flow'}
            >
              <InspectionDetails />
            </DashboardLayout>
          ),
        },
        {
          path: ':name/:id/nai-flow-view/inspections/list/:subName/:subId',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/schedule' },
                { title: 'Inspections List', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/list' },
                { title: ':subName', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/list' },
              ]}
              page={'nai-flow-view'}
            >
              <InspectionDetailsView />
            </DashboardLayout>
          ),
        },
        {
          path: ':name/:id/nai-flow/inspections/schedule',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow/Inspections/schedule' },
                { title: 'Schedule', redirect: '/project-management/:name/:id/nai-flow/Inspections/schedule' },
              ]}
              page={'inspections'}
              parentPage={'nai-flow'}
            >
              <Schedule />
            </TabsLayout>
          ),
        },
        {
          path: ':name/:id/nai-flow-view/inspections/schedule',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/schedule' },
                { title: 'Schedule', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/schedule' },
              ]}
              page={'inspections'}
              parentPage={'nai-flow-view'}
            >
              <ScheduleView />
            </TabsLayout>
          ),
        },
        {
          path: ':name/:id/nai-flow/inspections/schedule/add',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow/Inspections/schedule' },
                { title: 'Schedule', redirect: '/project-management/:name/:id/nai-flow/Inspections/schedule' },
                { title: 'Add Schedule', redirect: '/project-management/:name/:id/nai-flow/Inspections/schedule' },
              ]}
            >
              <AddInspectionSchedule />
            </DashboardLayout>
          ),
        },
        {
          path: ':name/:id/nai-flow-view/inspections/schedule/add',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/schedule' },
                { title: 'Schedule', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/schedule' },
                { title: 'Add Schedule', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/schedule' },
              ]}
            >
              <AddInspectionScheduleView />
            </DashboardLayout>
          ),
        },
        {
          path: ':name/:id/nai-flow/inspections/schedule/:subName/:subId',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow/Inspections/schedule' },
                { title: 'Schedule', redirect: '/project-management/:name/:id/nai-flow/Inspections/schedule' },
                { title: ':subName', redirect: '/project-management/:name/:id/nai-flow/Inspections/schedule' },
              ]}
              page={'nai-flow'}
            >
              <AddInspectionSchedule />
            </DashboardLayout>
          ),
        },
        {
          path: ':name/:id/nai-flow-view/inspections/schedule/:subName/:subId',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/schedule' },
                { title: 'Schedule', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/schedule' },
                { title: ':subName', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/schedule' },
              ]}
              page={'nai-flow-view'}
            >
              <AddInspectionScheduleView />
            </DashboardLayout>
          ),
        },
        {
          path: ':name/:id/nai-flow/inspections/non-conformance',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow/Inspections/schedule' },
                { title: 'Non-Conformance', redirect: '/project-management/:name/:id/nai-flow/Inspections/non-conformance' },
              ]}
              page={'inspections'}
              parentPage={'nai-flow'}
            >
              <NonConformance />
            </TabsLayout>
          ),
        },
        {
          path: ':name/:id/nai-flow-view/inspections/non-conformance',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/schedule' },
                { title: 'Non-Conformance', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/non-conformance' },
              ]}
              page={'inspections'}
              parentPage={'nai-flow-view'}
            >
              <NonConformanceView />
            </TabsLayout>
          ),
        },
        {
          path: ':name/:id/nai-flow/inspections/non-conformance/:subName/:subId',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow/Inspections/schedule' },
                { title: 'Non-Conformance', redirect: '/project-management/:name/:id/nai-flow/Inspections/non-conformance' },
                { title: ':subName', redirect: '/project-management/:name/:id/nai-flow/Inspections/non-conformance' },
              ]}
            >
              <NonConformanceDetails />
            </DashboardLayout>
          ),
        },
        {
          path: ':name/:id/nai-flow-view/inspections/non-conformance/:subName/:subId',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/schedule' },
                { title: 'Non-Conformance', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/non-conformance' },
                { title: ':subName', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/non-conformance' },
              ]}
            >
              <NonConformanceDetailsView />
            </DashboardLayout>
          ),
        },
        {
          path: ':name/:id/nai-flow/trainings',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                { title: 'Trainings', redirect: '/project-management/:name/:id/nai-flow/trainings' },
              ]}
              page={'nai-flow'}
            >
              <Trainings />
            </TabsLayout>
          ),
        },
        {
          path: ':name/:id/nai-flow-view/trainings',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                { title: 'Trainings', redirect: '/project-management/:name/:id/nai-flow-view/trainings' },
              ]}
              page={'nai-flow-view'}
            >
              <TrainingsView />
            </TabsLayout>
          ),
        },
        {
          path: ':name/:id/nai-flow/trainings/:subName/:subId',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                { title: 'Trainings', redirect: '/project-management/:name/:id/nai-flow/trainings' },
                { title: ':subName', redirect: '/project-management/:name/:id/nai-flow/trainings' },
              ]}
              page={'nai-flow'}
            >
              <TrainingDetails />
            </DashboardLayout>
          ),
        },
        {
          path: ':name/:id/nai-flow-view/trainings/:subName/:subId',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                { title: 'Trainings', redirect: '/project-management/:name/:id/nai-flow-view/trainings' },
                { title: ':subName', redirect: '/project-management/:name/:id/nai-flow-view/trainings' },
              ]}
              page={'nai-flow-view'}
            >
              <TrainingDetailsView />
            </DashboardLayout>
          ),
        },
        {
          path: ':name/:id/nai-flow/statistics-capture/schedule',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                { title: 'Statistics Capture', redirect: '/project-management/:name/:id/nai-flow/statistics-capture/schedule' },
                { title: 'Schedule', redirect: '/project-management/:name/:id/nai-flow/statistics-capture/schedule' },
              ]}
              page={'statistics-capture'}
              parentPage={'nai-flow'}
            >
              <StatisticsCapture />
            </TabsLayout>
          ),
        },
        {
          path: ':name/:id/nai-flow-view/statistics-capture/schedule',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                { title: 'Statistics Capture', redirect: '/project-management/:name/:id/nai-flow-view/statistics-capture/schedule' },
                { title: 'Schedule', redirect: '/project-management/:name/:id/nai-flow-view/statistics-capture/schedule' },
              ]}
              page={'statistics-capture'}
              parentPage={'nai-flow-view'}
            >
              <StatisticsCaptureView />
            </TabsLayout>
          ),
        },
        {
          path: ':name/:id/nai-flow/statistics-capture/schedule/add',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                { title: 'Statistics Capture', redirect: '/project-management/:name/:id/nai-flow/statistics-capture/schedule' },
                { title: 'Add Schedule', redirect: '/project-management/:name/:id/nai-flow/statistics-capture/schedule' },
              ]}
              page={'nai-flow'}
            >
              <AddSchedule />
            </DashboardLayout>
          ),
        },
        {
          path: ':name/:id/nai-flow-view/statistics-capture/schedule/add',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                { title: 'Statistics Capture', redirect: '/project-management/:name/:id/nai-flow-view/statistics-capture/schedule' },
                { title: 'Add Schedule', redirect: '/project-management/:name/:id/nai-flow-view/statistics-capture/schedule' },
              ]}
              page={'nai-flow-view'}
            >
              <AddScheduleView />
            </DashboardLayout>
          ),
        },
        {
          path: ':name/:id/nai-flow/statistics-capture/schedule/:subName/:subId',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                { title: 'Statistics Capture', redirect: '/project-management/:name/:id/nai-flow/statistics-capture/schedule' },
                { title: ':subName', redirect: '/project-management/:name/:id/nai-flow/statistics-capture/schedule' },
              ]}
              page={'nai-flow'}
            >
              <AddSchedule />
            </DashboardLayout>
          ),
        },
        {
          path: ':name/:id/nai-flow-view/statistics-capture/schedule/:subName/:subId',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                { title: 'Statistics Capture', redirect: '/project-management/:name/:id/nai-flow-view/statistics-capture/schedule' },
                { title: ':subName', redirect: '/project-management/:name/:id/nai-flow-view/statistics-capture/schedule' },
              ]}
              page={'nai-flow-view'}
            >
              <AddScheduleView />
            </DashboardLayout>
          ),
        },
        {
          path: ':name/:id/nai-flow/statistics-capture/reports',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                { title: 'Statistics Capture', redirect: '/project-management/:name/:id/nai-flow/statistics-capture/schedule' },
                { title: 'Reports', redirect: '/project-management/:name/:id/nai-flow/statistics-capture/reports' },
              ]}
              page={'statistics-capture'}
              parentPage={'nai-flow'}
            >
              <StatisticsCaptureReport />
            </TabsLayout>
          ),
        },
        {
          path: ':name/:id/nai-flow-view/statistics-capture/reports',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                { title: 'Statistics Capture', redirect: '/project-management/:name/:id/nai-flow-view/statistics-capture/schedule' },
                { title: 'Reports', redirect: '/project-management/:name/:id/nai-flow-view/statistics-capture/reports' },
              ]}
              page={'statistics-capture'}
              parentPage={'nai-flow-view'}
            >
              <StatisticsCaptureReportView />
            </TabsLayout>
          ),
        },
        {
          path: ':name/:id/nai-flow/statistics-capture/reports/:subName/:subId',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                { title: 'Statistics Capture', redirect: '/project-management/:name/:id/nai-flow/statistics-capture/schedule' },
                { title: 'Reports', redirect: '/project-management/:name/:id/nai-flow/statistics-capture/reports' },
                { title: ':subName', redirect: '/project-management/:name/:id/nai-flow/statistics-capture/reports' },
              ]}
              page={'nai-flow'}
            >
              <EditReport />
            </DashboardLayout>
          ),
        },
        {
          path: ':name/:id/nai-flow-view/statistics-capture/reports/:subName/:subId',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                { title: 'Statistics Capture', redirect: '/project-management/:name/:id/nai-flow-view/statistics-capture/schedule' },
                { title: 'Reports', redirect: '/project-management/:name/:id/nai-flow-view/statistics-capture/reports' },
                { title: ':subName', redirect: '/project-management/:name/:id/nai-flow-view/statistics-capture/reports' },
              ]}
              page={'nai-flow-view'}
            >
              <EditReportView />
            </DashboardLayout>
          ),
        },
        {
          path: ':name/:id/nai-hub',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Hub', redirect: '/project-management/:name/:id/nai-hub' },
              ]}
              page={'project-page'}
            >
              <NaiHub />
            </TabsLayout>
          ),
        },
        {
          path: ':name/:id/nai-hub/add',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Hub', redirect: '/project-management/:name/:id/nai-hub' },
                { title: 'Add nAI Hub', redirect: '/project-management/:name/:id/nai-hub' },
              ]}
            >
              <AddNaiHub />
            </DashboardLayout>
          ),
        },
        {
          path: ':name/:id/nai-hub/edit/:subName/:subId/:resourceName/:resourceId/details',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Hub', redirect: '/project-management/:name/:id/nai-hub' },
                { title: ':subName', redirect: '/project-management/:name/:id/nai-hub' },
              ]}
              page={'nai-hub-page'}
            >
              <AddNaiHub mode="detail" />
            </TabsLayout>
          ),
        },
        {
          path: ':name/:id/nai-hub/edit/:subName/:subId/:resourceName/:resourceId/nai-hub-dashboard',
          private: true,
          element: (
            <TabsLayout
              breadcrumbData={[
                { title: 'Project Management', redirect: '/project-management' },
                { title: ':name', redirect: '/project-management/:name/:id/details' },
                { title: 'nAI Hub', redirect: '/project-management/:name/:id/nai-hub' },
                { title: ':subName', redirect: '/project-management/:name/:id/nai-hub' },
              ]}
              page={'nai-hub-page'}
            >
              <NaiHubDashboard />
            </TabsLayout>
          ),
        },
      ],
    },
    // Masters
    {
      path: 'masters',
      private: true,
      element: <SidebarLayout />,
      children: [
        {
          path: '',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Masters', redirect: '/masters' },
                { title: 'Division', redirect: '/masters' },
              ]}
            >
              <Division />
            </DashboardLayout>
          ),
        },
        {
          path: 'hazard-type',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Masters', redirect: '/masters' },
                { title: 'Hazard Type', redirect: '/masters' },
              ]}
            >
              <HazardType />
            </DashboardLayout>
          ),
        },
        {
          path: 'root-cause',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Masters', redirect: '/masters' },
                { title: 'Root Cause', redirect: '/masters' },
              ]}
            >
              <RootCause />
            </DashboardLayout>
          ),
        },
        {
          path: 'severity',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Masters', redirect: '/masters' },
                { title: 'Severity', redirect: '/masters' },
              ]}
            >
              <Severity />
            </DashboardLayout>
          ),
        },
        {
          path: 'observation-status',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Masters', redirect: '/masters' },
                { title: 'Observation Status', redirect: '/masters' },
              ]}
            >
              <ObservationStatus />
            </DashboardLayout>
          ),
        },
        {
          path: 'statistics-capture-status',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Masters', redirect: '/masters' },
                { title: 'Statistics Capture Status', redirect: '/masters' },
              ]}
            >
              <StatisticsCaptureStatus />
            </DashboardLayout>
          ),
        },
        {
          path: 'statistics-capture-inputs',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Masters', redirect: '/masters' },
                { title: 'Statistics Capture Inputs', redirect: '/masters' },
              ]}
            >
              <StatisticsCaptureInputs />
            </DashboardLayout>
          ),
        },
        {
          path: 'incident-status',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Masters', redirect: '/masters' },
                { title: 'Incident Status', redirect: '/masters' },
              ]}
            >
              <IncidentStatus />
            </DashboardLayout>
          ),
        },
        {
          path: 'incident-category',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Masters', redirect: '/masters' },
                { title: 'Incident Category', redirect: '/masters' },
              ]}
            >
              <IncidentCategory />
            </DashboardLayout>
          ),
        },
        {
          path: 'incident-severity',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Masters', redirect: '/masters' },
                { title: 'Incident Severity', redirect: '/masters' },
              ]}
            >
              <IncidentSeverity />
            </DashboardLayout>
          ),
        },
        {
          path: 'inspection-status',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Masters', redirect: '/masters' },
                { title: 'Inspection Status', redirect: '/masters' },
              ]}
            >
              <InspectionStatus />
            </DashboardLayout>
          ),
        },
        {
          path: 'inspection-schedule-status',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Masters', redirect: '/masters' },
                { title: 'Inspection Schedule Status', redirect: '/masters' },
              ]}
            >
              <InspectionScheduleStatus />
            </DashboardLayout>
          ),
        },
        {
          path: 'inspection-non-conformance-status',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Masters', redirect: '/masters' },
                { title: 'Inspection Non Conformance Status', redirect: '/masters' },
              ]}
            >
              <InspectionNonConformanceStatus />
            </DashboardLayout>
          ),
        },
        {
          path: 'inspection-sub-category',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Masters', redirect: '/masters' },
                { title: 'Inspection Sub Category', redirect: '/masters' },
              ]}
            >
              <InspectionSubCategory />
            </DashboardLayout>
          ),
        },
        {
          path: 'inspection-topic',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Masters', redirect: '/masters' },
                { title: 'Inspection Topic', redirect: '/masters' },
              ]}
            >
              <InspectionTopic />
            </DashboardLayout>
          ),
        },
        {
          path: 'inspection-questions',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Masters', redirect: '/masters' },
                { title: 'Inspection Questions', redirect: '/masters' },
              ]}
            >
              <InspectionQuestions />
            </DashboardLayout>
          ),
        },
        {
          path: 'training-category',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Masters', redirect: '/masters' },
                { title: 'Training Category', redirect: '/masters' },
              ]}
            >
              <TrainingCategory />
            </DashboardLayout>
          ),
        },
      ],
    },
    // Workflow Builder
    {
      path: 'workflow-builder',
      private: true,
      element: <SidebarLayout />,
      children: [
        {
          path: '',
          private: true,
          element: (
            <DashboardLayout breadcrumbData={[{ title: 'Workflow Builder', redirect: '/workflow-builder' }]}>
              <WorkflowBuilder />
            </DashboardLayout>
          ),
        },
        {
          path: 'edit/:formId',
          private: true,
          element: (
            <DashboardLayout
              breadcrumbData={[
                { title: 'Workflow Builder', redirect: '/workflow-builder' },
                { title: 'Edit', redirect: '' },
              ]}
            >
              <EditWorkflow />
            </DashboardLayout>
          ),
        },
      ],
    },
    // AI RAMS Assessment
    {
      path: 'ai-rams-assessment',
      private: true,
      element: <SidebarLayout />,
      children: [
        {
          path: '',
          private: true,
          element: (
            <DashboardLayout breadcrumbData={[{ title: 'AI RAMS Assessment', redirect: '/ai-rams-assessment' }]}>
              <AiRamsAssessment />
            </DashboardLayout>
          ),
        },
      ],
    },
  ]),
  {
    path: 'check-app',
    element: (
      <LRFLayout>
        <CheckAppInstalled />
      </LRFLayout>
    ),
  },
  {
    path: 'sso',
    private: false,
    element: <SSO />,
  },
  {
    path: '*',
    element: <PageNotFound />,
  },
]
